<template>
  <div class="root">
    <div class="header">
      <div class="header-item" :class="{ active: isModeAssociatedNovels }" @click="onClickAssociatedNovels">
        {{ $t('nolaNovelPage.postedWorks', { count: associatedNovels.length }) }}
      </div>
      <div class="header-item" :class="{ active: !isModeAssociatedNovels }" @click="onClickNonAssociatedNovels">
        {{ $t('nolaNovelPage.unpostedWorks', { count: nonAssociatedNovels.length }) }}
      </div>
    </div>
    <div class="body">
      <vue-loading v-if="isLoading" type="spiningDubbles" color="#efad4c" :size="{ width: '50px', height: '50px' }" />

      <template v-else-if="isModeAssociatedNovels && associatedNovels.length">
        <NolaNovelAssociatedNovelListItem
          v-for="novel in associatedNovels"
          :key="novel.novelId"
          :novel="novel"
          :novelFromNolaNovel="novelFromNolaNovel(novel)"
        />
      </template>
      <template v-else-if="!isModeAssociatedNovels && nonAssociatedNovels.length">
        <NolaNovelNonAssociatedNovelListItem v-for="novel in nonAssociatedNovels" :key="novel.novelId" :novel="novel" />
      </template>
      <template v-else>
        <NolaNovelEmptyList
          :isModeAssociatedNovels="isModeAssociatedNovels"
          @clickNonAssociatedNovels="onClickNonAssociatedNovels"
        />
      </template>
    </div>
    <BeginnerIconOnboarding v-if="showBeginnerIconOnboarding" />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { VueLoading } from "vue-loading-template";
import NolaNovelAssociatedNovelListItem from "@/components/molecules/NolaNovelAssociatedNovelListItem.vue";
import NolaNovelNonAssociatedNovelListItem from "@/components/molecules/NolaNovelNonAssociatedNovelListItem.vue";
import NolaNovelEmptyList from "@/components/molecules/NolaNovelEmptyList.vue";
import { AnalyticsDataType, AnalyticsTerm, ConnectedOtherServices, Novel, NovelFromNolaNovel } from "@/lib/models";
import BeginnerIconOnboarding from "@/components/organisms/BeginnerIconOnboarding.vue";

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    VueLoading,
    NolaNovelAssociatedNovelListItem,
    NolaNovelNonAssociatedNovelListItem,
    NolaNovelEmptyList,
    BeginnerIconOnboarding,
  },
  async created() {
    try {
      this.isLoading = true;

      const novelInitialize = this.$store.dispatch("novelModule/initialize");
      const userInitialize = this.$store.dispatch("userModule/initialize");
      await Promise.all([novelInitialize, userInitialize]);

      // 商業道バナーを押下したことがあり、初心者アイコンオンボーディングポップアップを表示していない場合
      if (this.hasUsedCommerceBanner && !this.hasDisplayedBeginnerIconOnboardingPopup) {
        this.showBeginnerIconOnboarding = true;

        // 初心者アイコンオンボーディングポップアップ表示フラグを立てる
        this.$store.dispatch("generalModule/setDisplayedBeginnerIconOnboardingPopup", true);
      }

      const connectedOtherServices: ConnectedOtherServices = this.$store.getters["userModule/connectedOtherServices"];
      if (!connectedOtherServices.nolaNovel.result) return;

      const novelsFromNolaNovel: NovelFromNolaNovel[] = await this.$store.dispatch("nolaNovelModule/fetchNovels");
      if (novelsFromNolaNovel.length === 0) return;

      await this.$store.dispatch(
        "nolaNovelModule/fetchAnalytics",
        novelsFromNolaNovel.flatMap((value) => [
          {
            targetId: value.id,
            type: AnalyticsDataType.PV_EPISODE,
            term: AnalyticsTerm.ALL,
          },
          {
            targetId: value.id,
            type: AnalyticsDataType.REVIEW,
            term: AnalyticsTerm.ALL,
          },
          {
            targetId: value.id,
            type: AnalyticsDataType.BOOKMARK,
            term: AnalyticsTerm.ALL,
          },
          {
            targetId: value.id,
            type: AnalyticsDataType.COMMENT,
            term: AnalyticsTerm.ALL,
          },
        ])
      );
    } finally {
      this.isLoading = false;
    }
  },
  mounted() {
    if (this.associatedNovels.length > 0) this.isModeAssociatedNovels = true;
  },
  data() {
    return {
      isLoading: true,
      isModeAssociatedNovels: false,
      showBeginnerIconOnboarding: false,
    };
  },
  computed: {
    novels() {
      return this.$store.getters["novelModule/novels"];
    },
    novelsFromNolaNovel() {
      return this.$store.getters["nolaNovelModule/novels"];
    },
    novelFromNolaNovel() {
      return (novel) => {
        const { id } = novel.associatedData!.nolaNovel;
        return this.novelsFromNolaNovel.find((value) => value.id === id);
      };
    },
    associatedNovels() {
      const { novels, novelFromNolaNovel } = this;
      return novels.filter((novel) => novel.associatedData && novelFromNolaNovel(novel));
    },
    nonAssociatedNovels() {
      const { novels, novelFromNolaNovel } = this;
      return novels.filter((novel) => !novel.associatedData || !novelFromNolaNovel(novel));
    },
    hasUsedCommerceBanner() {
      return this.$store.getters["generalModule/hasUsedCommerceBanner"];
    },
    hasDisplayedBeginnerIconOnboardingPopup() {
      return this.$store.getters["generalModule/hasDisplayedBeginnerIconOnboardingPopup"];
    },
  },
  methods: {
    onClickAssociatedNovels() {
      this.isModeAssociatedNovels = true;
    },
    onClickNonAssociatedNovels() {
      this.isModeAssociatedNovels = false;
    },
  },
});

interface Props {}

interface Data {
  isLoading: boolean;
  isModeAssociatedNovels: boolean;
  showBeginnerIconOnboarding: boolean;
}

interface Computed {
  novels: Novel[];
  novelsFromNolaNovel: NovelFromNolaNovel[];
  novelFromNolaNovel(novel: Novel): NovelFromNolaNovel | undefined;
  associatedNovels: Novel[];
  nonAssociatedNovels: Novel[];
  hasUsedCommerceBanner: boolean;
  hasDisplayedBeginnerIconOnboardingPopup: boolean;
}

interface Methods {
  onClickAssociatedNovels(): void;
  onClickNonAssociatedNovels(): void;
}
</script>

<style lang="scss" scoped>
.root {
  height: calc(100vh - #{$header-height});
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
  display: flex;
  flex-direction: column;
}

.header {
  background-color: var(--color-surface-subtle);
  display: flex;
  box-sizing: border-box;
  padding: 20px;
  box-shadow: var(--shadow-sm);
  z-index: 1;

  .header-item {
    margin: 0 10px;
    font-weight: var(--font-weight-bold);
    font-size: 1.2em;
    cursor: pointer;
    color: var(--color-text-low);

    &.active {
      color: var(--color-primary);
    }
  }
}

.body {
  box-sizing: border-box;
  padding: 30px;
  overflow: auto;
  color: var(--color-text-normal);
}
</style>
